import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Data } from "@angular/router";
import { takeUntil } from "rxjs/operators";
import { AppLayout } from "../app-layout.types";
import {MojoRouterEventsService} from "@routing/mojo-router-events.service";
import {OnDestroySubject} from "@common/components/on-destroy-subject";

interface RouteData extends Data {
  layout?: AppLayout;
}

/**
 * Layout component that dynamically updates based on route configuration.
 * Supports nested route layouts with override capability at any route level.
 */
@Component({
  selector: "app-layout",
  templateUrl: "./app-layout.component.html",
  styleUrls: ["./app-layout.component.scss"]
})
export class AppLayoutComponent
  extends OnDestroySubject
  implements OnInit {

  layout: AppLayout = "original";

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    routerEvents: MojoRouterEventsService) {
    super();

    routerEvents.NavigationEnd$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.updateLayout());

  }

  ngOnInit(): void {
    this.updateLayout();
  }

  /**
   * Updates the layout based on route configuration.
   * Traverses the route tree to find the most specific layout configuration.
   */
  private updateLayout(): void {
    const newLayout = this.resolveLayoutFromRoute(this.activatedRoute);
    if (newLayout !== this.layout) {
      this.layout = newLayout;
    }
  }

  /**
   * Resolves the layout configuration from the current route tree.
   * @param route The activated route to start resolving from
   * @returns The resolved layout configuration
   */
  private resolveLayoutFromRoute(route: ActivatedRoute): AppLayout {
    // Get the deepest active route
    let currentRoute = route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    // Iterate through the paths and change the layout as we find
    // a config for it.
    //
    // The reason we do this is that there might be empty grouping
    // paths or componentless routes along the path. Because of that,
    // we cannot just assume that the layout configuration will be
    // in the last path's config or in the first path's config.
    //
    // So, we get all the paths that matched starting from root all
    // the way to the current activated route, walk through them one
    // by one and change the layout as we find the layout config. This
    // way, layout configuration can live anywhere within the path and
    // we won't miss it.
    //
    // Also, this will allow overriding the layout in any time so we
    // can have different layouts for different routes.
    return currentRoute.pathFromRoot.reduce((layout, path) => {
        const routeConfig = path.routeConfig;
        const routeData = routeConfig?.data as RouteData | undefined;
        return routeData?.layout ?? layout;
      }, this.layout);
  }
}
